/**
 * Shared auth screen primitives — Field, SSOButton, Divider, brand marks.
 * Used by SignInPage, RequestAccessPage.
 */

import { type ReactNode } from 'react'

/* ---------- Brand SSO marks ---------- */

export function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}

export function AppleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  )
}

export function MicrosoftMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
      <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
    </svg>
  )
}

export function MagicLinkMark({ dark = false }: { dark?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dark ? '#E8E1D2' : 'currentColor'} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M4 12h10M4 17h16" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  )
}

/* ---------- Form field ---------- */

interface FieldProps {
  label: string
  name?: string
  type?: 'text' | 'email' | 'password'
  placeholder?: string
  hint?: string
  value?: string
  defaultValue?: string
  autoComplete?: string
  required?: boolean
  dark?: boolean
  rows?: number
  textarea?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
}

export function AuthField({
  label,
  name,
  type = 'text',
  placeholder,
  hint,
  value,
  defaultValue = '',
  autoComplete,
  required = false,
  dark = false,
  textarea = false,
  rows = 3,
  onChange,
}: FieldProps) {
  const inputStateProps = value === undefined
    ? { defaultValue, onChange }
    : { value, onChange }

  const baseInputStyle = {
    padding: '12px 14px',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-sans)',
    background: dark ? 'rgba(255,255,255,0.04)' : 'var(--earth-bg)',
    color: dark ? '#E8E1D2' : 'var(--earth-text-primary)',
    border: `1px solid ${dark ? '#3D3629' : 'var(--earth-border)'}`,
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    transition: 'border-color var(--motion-fast)',
  } as const

  return (
    <label style={{
      display: 'flex', flexDirection: 'column', gap: 6,
      fontFamily: 'var(--font-sans)',
    }}>
      <span style={{
        fontSize: 'var(--text-sm)', fontWeight: 500,
        color: dark ? '#B8AE9C' : 'var(--earth-text-secondary)',
      }}>{label}</span>
      {textarea ? (
        <textarea
          name={name}
          {...inputStateProps}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          rows={rows}
          style={{ ...baseInputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          name={name}
          type={type}
          {...inputStateProps}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          style={baseInputStyle}
        />
      )}
      {hint && (
        <span style={{ fontSize: 'var(--text-xs)', color: dark ? '#887E6C' : 'var(--earth-text-muted)' }}>
          {hint}
        </span>
      )}
    </label>
  )
}

/* ---------- SSO button ---------- */

interface SSOButtonProps {
  icon: ReactNode
  children: ReactNode
  dark?: boolean
  onClick?: () => void
}

export function SSOButton({ icon, children, dark = false, onClick }: SSOButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 10,
        padding: '12px 16px',
        width: '100%',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)', fontWeight: 500,
        color: dark ? '#E8E1D2' : 'var(--earth-text-primary)',
        background: dark ? 'rgba(255,255,255,0.04)' : 'var(--earth-bg)',
        border: `1px solid ${dark ? '#3D3629' : 'var(--earth-border)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'all var(--motion-fast)',
      }}
    >
      {icon}<span>{children}</span>
    </button>
  )
}

/* ---------- Divider ---------- */

interface DividerProps {
  label?: string
  dark?: boolean
}

export function AuthDivider({ label = 'or', dark = false }: DividerProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
      color: dark ? '#887E6C' : 'var(--earth-text-muted)',
      fontSize: 'var(--text-xs)', letterSpacing: '0.12em',
      textTransform: 'uppercase',
    }}>
      <div style={{ flex: 1, height: 1, background: dark ? '#3D3629' : 'var(--earth-border)' }} />
      <span>{label}</span>
      <div style={{ flex: 1, height: 1, background: dark ? '#3D3629' : 'var(--earth-border)' }} />
    </div>
  )
}
